import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { apiCall } from "utils/apiClient";
import { FaEye, FaSearch } from "react-icons/fa";
import Pagination from "components/pagination";

function ServiceGallery() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  
  const navigate = useNavigate();

  const getAllServices = async () => {
    setLoading(true);
    try {
      const res = await apiCall({
        endpoint: "service-gallery/services/stats",
        method: "GET",
      });
      if (res?.status === 200) {
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        setServices(
          raw.map((s) => ({
            id: s.id,
            name: s.name || s.service_name,
            imagesCount: s.image_count || 0,
            videosCount: s.video_count || 0,
            tutorialVideosCount: s.tutorial_count || 0,
          }))
        );
      }
    } catch (err) {
      console.error("Failed to load services", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllServices();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordsPerPage]);

  const handleView = (id) => {
    navigate(`/service-gallery/view/${id}`);
  };

  const filteredServices = services.filter((service) =>
    service.name?.toLowerCase().includes((searchTerm || "").toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredServices.length / recordsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * recordsPerPage;
  const paginatedServices = filteredServices.slice(startIndex, startIndex + recordsPerPage);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="ml-auto flex w-full max-w-md items-start gap-2">
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-3 p-2.5"
              placeholder="Search Services"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {loading ? (
          <p className="text-center">Loading...</p>
        ) : (
          <div className="space-y-3">
            <div className="overflow-x-auto bg-white rounded-xl shadow">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 text-gray-600 uppercase text-[11px]">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2">Images</th>
                  <th className="px-3 py-2">Tutorial Videos</th>
                  <th className="px-3 py-2">Videos</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedServices.map((s, i) => (
                  <tr key={s.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{startIndex + i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {s.name}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
                        {s.imagesCount}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                        {s.tutorialVideosCount}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                        {s.videosCount}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex flex-wrap justify-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700 transition hover:bg-sky-100"
                          onClick={() => handleView(s.id)}
                        >
                          <FaEye />
                          View
                        </button>
                        {/* Delete removed per request */}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredServices.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-gray-400">
                      No services found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination Component */}
            <Pagination
              currentPage={safeCurrentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              recordsPerPage={recordsPerPage}
              onRecordsPerPageChange={setRecordsPerPage}
              totalRecords={filteredServices.length}
              count={paginatedServices.length}
            showFirstLastControls
            compact
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default ServiceGallery;
